import Like from "@/components/Svg/Like";
import Store from "@/components/Svg/Store";
import { LikeProduct } from "@/services/likeProductService";
import { useMutation } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";
import { handleAddToCart } from "@/function/addCart";
import { toast } from "react-toastify";
import useCartStore from "@/store/store";
import { Add, Minus } from "iconsax-react";
import { TargetProductType } from "@/types/productType";
import useSoftSetting from "@/hooks/useSoftSetting";

interface CardProps {
  hasBuy?: boolean;
  className?: string;
  id?: number;
  productName?: string;
  categoryName?: string | null;
  categoryId?: number | null;
  originalPrice?: number;
  salePrice?: number | null;
  disCountAmount?: number;
  disCountPrecent?: number;
  image?: string;
  productId?: number;
  variantId?: number;
  disCountEndDate?: Date;
  isFavorite?: boolean;
  stock?: number | null;
  classNameImage?: string;
  refetch?: () => void;
  stockThreshold?: number | 0 | undefined;
  unitCounting?: number;
}

const formatNumber = (num: number): string => {
  return num < 10 ? `0${num}` : `${num}`;
};
function Card({
  hasBuy,
  className,
  productId,
  variantId,
  productName,
  categoryName,
  stock,
  disCountEndDate,
  originalPrice,
  salePrice,
  disCountPrecent,
  image,
  isFavorite,
  classNameImage,
  unitCounting,
  stockThreshold,
  refetch,
}: CardProps) {
  const accessToken = Cookies.get("AccessToken");
  const [isImageHover, setIsImageHover] = useState(false);
  const customerId = Cookies.get("mobileNumber");
  const { data } = useSoftSetting();
  const { mutateAsync: getLikeProductCard } = useMutation({
    mutationFn: LikeProduct,
    retry: 2,
    retryDelay: 1000,
  });

  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
    days: 0,
  });

  const calculateTimeLeft = useCallback(() => {
    const now = new Date().getTime();
    const endDate = disCountEndDate ? new Date(disCountEndDate).getTime() : now;
    const difference = endDate - now;

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const totalSeconds = Math.floor(difference / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { days, hours, minutes, seconds };
  }, [disCountEndDate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  const formatTimeLeft = () => {
    if (timeLeft.days > 0) {
      return `${timeLeft.days} روز و ${formatNumber(timeLeft.hours)} ساعت`;
    } else {
      return `${formatNumber(timeLeft.hours)}:${formatNumber(
        timeLeft.minutes
      )}:${formatNumber(timeLeft.seconds)}`;
    }
  };

  const { productCard, loadCard, updateQuantity, removeFromCard } =
    useCartStore();
  const [targetProduct, setTargetProduct] = useState<TargetProductType | null>(
    null
  );
  const [isLiked, setIsLiked] = useState<boolean>(false);

  const LikeProductHandler = useCallback(async () => {
    if (!accessToken) {
      toast.warning("لطفا وارد حساب کاربری خود شوید");
    } else {
      try {
        const response = await getLikeProductCard({
          customerId: Number(customerId),
          variantId: Number(variantId),
        });
        if (response?.isSuccess) {
          setIsLiked(response?.data);
          toast.success(response?.message);
          if (refetch) {
            refetch();
          }
        }
      } catch (err) {
        console.log("error in fetch all data for last offers ", err);
      }
    }
  }, [getLikeProductCard, customerId, productId, accessToken, refetch]);

  useEffect(() => {
    if (productCard?.length) {
      const foundProduct = productCard?.find(
        (product) =>
          product?.productId === productId && product?.variantId === variantId
      );
      setTargetProduct(foundProduct || null);
    } else {
      setTargetProduct(null);
    }
  }, [productCard, productId, variantId]);

  const stockProduct =
    Number(stock) > Number(stockThreshold)
      ? Number(stockThreshold)
      : Number(stock);

  const increment = () => {
    const newQuantity = Math.min(
      (targetProduct?.quantity ?? 1) + 1,
      stockProduct
    );
    updateQuantity(
      Number(targetProduct?.productId),
      Number(targetProduct?.variantId),
      newQuantity,
      stockProduct
    );
  };
  const decrement = () => {
    const currentQuantity = targetProduct?.quantity ?? 1;
    const newQuantity = currentQuantity - 1;
    if (newQuantity < 1) {
      deleteProductHandler();
    } else {
      updateQuantity(
        Number(targetProduct?.productId),
        Number(targetProduct?.variantId),
        newQuantity,
        stockProduct
      );
    }
  };
  const deleteProductHandler = () => {
    if (targetProduct?.productId) {
      removeFromCard(targetProduct.variantId);
      setTargetProduct(null);
    }
  };

  useEffect(() => {
    loadCard();
  }, [loadCard]);

  return (
    <>
      <div
        className={`bg-white rounded-[2rem] text-ws-gray-800 border-[3px] flex flex-col justify-between  border-ws-white-100 w-[300px] xxl:h-[380px] h-[380px] ${className} `}
        onMouseEnter={() => setIsImageHover(true)}
        onMouseLeave={() => setIsImageHover(false)}
      >
        <div className="flex flex-col justify-between">
          <div
            className={`card-header  items-center justify-center object-cover bg-white   ${
              hasBuy ? "h-[220px]" : "h-[220px] "
            }  cursor-pointer   relative card-image ${classNameImage}`}
          >
            <Link
              className="w-full h-full flex justify-center"
              href={`/product/productId=${productId}&&variantid=${variantId}`}
              target="_blank"
            >
              {image ? (
                isImageHover ? (
                  <div className="relative w-full h-full">
                    <Image
                      width={1200}
                      height={600}
                      className="w-full h-full object-cover"
                      src={`${image}`}
                      alt=""
                      unoptimized
                    />
                    <span className="mask absolute top-0 left-0 w-full h-full"></span>
                  </div>
                ) : (
                  <Image
                    priority
                    fetchPriority="high"
                    sizes="100vw"
                    width={1200}
                    height={600}
                    className="w-full h-auto object-cover p-6"
                    src={`${image}`}
                    alt=""
                    quality={100}
                    unoptimized
                  />
                )
              ) : (
                <Image
                  priority
                  fetchPriority="high"
                  sizes="100vw"
                  width={1200}
                  height={600}
                  className="w-full h-auto object-cover p-6"
                  src={`${process.env.NEXT_PUBLIC_ADMIN_URL}placeholder.png`}
                  alt="No image available"
                  unoptimized
                />
              )}
            </Link>

            <div
              className={`flex ${
                disCountPrecent !== 0 || timeLeft?.seconds !== 0
                  ? "justify-between"
                  : "justify-end"
              } text-xs items-center absolute w-full top-3 left-0 px-6 py-2 rtl`}
            >
              {timeLeft.hours === 0 &&
              timeLeft.minutes === 0 &&
              timeLeft.seconds === 0 ? (
                ""
              ) : (
                <div className="flex items-center justify-between gap-3">
                  {disCountPrecent !== 0 ? (
                    <span className="num-vazir bg-ws-coral-blush-400 text-ws-white-100 rounded-[10px] px-2 py-1">
                      {disCountPrecent}%
                    </span>
                  ) : (
                    ""
                  )}

                  <span className="num-vazir text-xs text-ws-coral-blush-400">
                    {formatTimeLeft()}
                  </span>
                </div>
              )}
              <Like
                onClick={() => {
                  LikeProductHandler();
                }}
                className={`cursor-pointer ${
                  isImageHover
                    ? isLiked || isFavorite
                      ? "fill-[#cf6464] hover-like"
                      : "hover-like"
                    : ""
                } ${
                  isLiked || isFavorite
                    ? "fill-[#CF6464]"
                    : "fill-[rgba(255,255,255,0.89)]"
                }`}
              />
            </div>
          </div>
          <Link
            target="_blank"
            href={`/product/productId=${productId}&&variantid=${variantId}`}
          >
            <div
              className={` card-body flex flex-col gap-1 px-5 text-right  ${
                hasBuy ? "mt-4" : "pb-6  mt-5"
              }`}
            >
              <h6 className="text-ws-gray-500 text-sm font-extralight ">
                {categoryName}
              </h6>
              <h2 className="text-ws-gray-600 text-base peydaM">
                {productName}
              </h2>
            </div>
          </Link>
        </div>

        <div
          className={`card-footer ${
            hasBuy ? "flex" : "hidden"
          }   items-center justify-between ${
            disCountPrecent ? "mt-0" : "mt-2"
          }  px-5 pb-3  `}
        >
          {stock !== 0 ? (
            <>
              <Link
                href={`/product/productId=${productId}&&variantid=${variantId}`}
              >
                <div className="flex items-center gap-2 justify-end self-end  ltr">
                  <div className="flex flex-col items-center">
                    {originalPrice !== salePrice ? (
                      <span className="line-through text-ws-gray-300 flex items-center flex-row-reverse gap-1 font-medium num-vazir">
                        {originalPrice !== 0
                          ? originalPrice?.toLocaleString()
                          : "-"}
                        <span>{data?.currencyUnit} </span>
                      </span>
                    ) : (
                      ""
                    )}
                    <span className="text-ws-gray-600 flex items-center flex-row-reverse gap-1 font-bold text-lg num-vazir ">
                      {salePrice !== 0 ? salePrice?.toLocaleString() : "-"}
                      <span className="text-ws-gray-500 font-medium text-sm">
                        {data?.currencyUnit}
                      </span>
                    </span>
                  </div>
                </div>
              </Link>

              {targetProduct ? (
                <div className="inline-flex items-center border border-ws-gray-300 rounded-xl ltr p-1 py-1 ">
                  <button
                    onClick={decrement}
                    className="w-8 h-8 flex items-center justify-center hover:text-gray-600 transition-colors"
                    aria-label="کاهش تعداد"
                  >
                    <Minus
                      color="#4B5D6C"
                      className="w-4 h-4 font-bold path-stroke"
                    />
                  </button>

                  <span className="w-max text-xs text-center font-medium flex justify-center   rtl py-1 num-vazir">
                    {` ${targetProduct?.quantity} ${unitCounting}`}
                  </span>

                  <button
                    onClick={increment}
                    className="w-6 h-6 flex items-center justify-center hover:text-gray-600 transition-colors"
                    aria-label="افزایش تعداد"
                  >
                    <Add
                      color="#4B5D6C"
                      className="w-4 h-4 font-bold path-stroke"
                    />
                  </button>
                </div>
              ) : (
                <span
                  onClick={() =>
                    handleAddToCart(
                      Number(productId),
                      Number(variantId),
                      String(productName),
                      String(image),
                      Number(salePrice),
                      Number(originalPrice),
                      String(unitCounting),
                      Number(stockThreshold)
                    )
                  }
                  className="bg-ws-gray-200 rounded-lg p-2 px-4 flex items-center justify-center cursor-pointer"
                >
                  <Store />
                </span>
              )}
            </>
          ) : (
            <span className="text-ws-coral-blush-400 text">ناموجود</span>
          )}
        </div>
      </div>
    </>
  );
}

export default Card;
